/**
 * Overlay (Dialog) da conversa por voz em tempo real.
 *
 * Abre a partir do botão de microfone do composer, inicia a sessão Gemini Live
 * (via `useVoiceSession`) e mostra: indicador de estado, transcrição ao vivo,
 * cronômetro decorrido e o botão de encerrar. Fechar o Dialog (X/ESC/clique
 * fora) SEMPRE chama `stop()` — o microfone nunca fica aberto.
 */
import { getPersonaForVoice } from '@agente/shared'
import { ShieldAlert } from 'lucide-react'
import { useEffect } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { VoiceIndicator } from '@/components/voice/voice-indicator'
import { useUserDoc } from '@/hooks/useUserDoc'
import { useVoiceSession, type VoiceSessionStatus } from '@/hooks/useVoiceSession'
import { formatElapsed } from '@/hooks/voice-session-utils'

export interface VoiceOverlayProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  conversationId: string | null
  /** Chamado quando a sessão cria uma conversa nova (o ChatView guarda o id e navega ao fechar). */
  onConversationCreated?: (conversationId: string) => void
}

function statusLabel(status: VoiceSessionStatus, toolLabel: string | null): string {
  switch (status) {
    case 'requesting-mic':
      return 'Aguardando permissão do microfone…'
    case 'connecting':
      return 'Conectando…'
    case 'reconnecting':
      return 'Reconectando…'
    case 'listening':
      return 'Ouvindo…'
    case 'speaking':
      return 'Falando…'
    case 'executing-tool':
      return `Executando: ${toolLabel ?? 'ferramenta'}`
    case 'awaiting-confirmation':
      return 'Aguardando sua confirmação'
    case 'ending':
      return 'Encerrando…'
    case 'ended':
      return 'Sessão encerrada.'
    case 'error':
      return 'Ocorreu um erro.'
    default:
      return 'Pronto para começar.'
  }
}

export function VoiceOverlay({
  open,
  onOpenChange,
  conversationId,
  onConversationCreated,
}: VoiceOverlayProps) {
  const { userDoc } = useUserDoc()
  const persona = getPersonaForVoice(userDoc?.preferences?.voiceName)
  const artigo = persona.gender === 'feminina' ? 'a' : 'o'
  const papel = persona.gender === 'feminina' ? 'a assistente executiva' : 'o assistente executivo'

  const {
    status,
    error,
    toolLabel,
    elapsed,
    transcript,
    toolsEnabled,
    transcriptionEnabled,
    modelDowngraded,
    pendingConfirmation,
    confirmPendingAction,
    cancelPendingAction,
    start,
    stop,
  } = useVoiceSession({
    onConversationCreated,
  })

  // Inicia ao abrir; garante o stop ao fechar por qualquer via. `start` é
  // idempotente (no-op com sessão ativa). A navegação para /conversations/$id é
  // adiada pelo ChatView até o fechamento do overlay, então `conversationId` não
  // muda no meio da sessão e este efeito não a interrompe.
  useEffect(() => {
    if (open) {
      void start(conversationId ?? undefined)
    } else {
      void stop()
    }
  }, [open, conversationId, start, stop])

  const active =
    status === 'listening' ||
    status === 'speaking' ||
    status === 'executing-tool' ||
    status === 'awaiting-confirmation' ||
    status === 'connecting' ||
    status === 'reconnecting' ||
    status === 'requesting-mic'

  // Encerra a sessão ANTES de propagar o fechamento ao pai. O ChatView adia a
  // navegação para o fechamento do overlay, e navegar remonta a rota (`/` e
  // `/conversations/$id` são componentes distintos), o que mataria a LiveSession
  // se ainda estivesse ativa. Aguardar stop() aqui garante que a sessão já
  // encerrou (mic liberado + turnos persistidos) quando o pai fecha e navega.
  async function handleClose() {
    await stop()
    onOpenChange(false)
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Fechar por X/ESC/clique-fora passa pelo mesmo caminho: encerra a sessão
        // antes de fechar. (Abrir é controlado pelo pai; não há trigger interno.)
        if (!next) void handleClose()
      }}
    >
      <DialogContent className="sm:max-w-md" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            {`Conversa por voz com ${artigo} ${persona.name}`}
          </DialogTitle>
          <DialogDescription>
            {`Fale naturalmente com ${artigo} ${persona.name}, ${papel}. A conversa é transcrita para o histórico.`}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center gap-4 py-2">
          <VoiceIndicator status={status} />

          <p className="text-sm font-medium" role="status">
            {statusLabel(status, toolLabel)}
          </p>

          {error ? (
            <div className="border-destructive/40 bg-destructive/10 text-destructive w-full rounded-lg border px-3 py-2 text-center text-sm">
              {error}
            </div>
          ) : null}

          {pendingConfirmation ? (
            <div className="border-primary/40 bg-primary/[0.05] w-full space-y-3 rounded-lg border px-3 py-3">
              <div className="flex items-start gap-2">
                <ShieldAlert className="text-primary mt-0.5 size-4 shrink-0" />
                <div className="min-w-0 flex-1 space-y-0.5">
                  <p className="text-foreground text-xs font-semibold tracking-wide uppercase">
                    Confirme para prosseguir
                  </p>
                  <p className="text-foreground/90 text-sm">{pendingConfirmation.summary}</p>
                </div>
              </div>
              <div className="flex justify-center gap-2">
                <Button size="sm" onClick={confirmPendingAction}>
                  Confirmar
                </Button>
                <Button size="sm" variant="outline" onClick={cancelPendingAction}>
                  Cancelar
                </Button>
              </div>
            </div>
          ) : null}

          {status === 'reconnecting' ? (
            <div className="border-border/70 bg-muted/40 text-muted-foreground w-full rounded-lg border px-3 py-2 text-center text-xs">
              Conexão instável; retomando a conversa sem perder o contexto…
            </div>
          ) : null}

          {active && !toolsEnabled ? (
            <div className="border-border/70 bg-muted/40 text-muted-foreground w-full rounded-lg border px-3 py-2 text-center text-xs">
              Modo de voz sem ferramentas (agenda e lembretes indisponíveis nesta sessão).
            </div>
          ) : null}

          {active && !transcriptionEnabled ? (
            <div className="border-border/70 bg-muted/40 text-muted-foreground w-full rounded-lg border px-3 py-2 text-center text-xs">
              Transcrição indisponível nesta sessão; apenas o registro da conversa será salvo no
              histórico.
            </div>
          ) : null}

          {active && modelDowngraded ? (
            <div className="border-border/70 bg-muted/40 text-muted-foreground w-full rounded-lg border px-3 py-2 text-center text-xs">
              Modelo selecionado indisponível; usando o padrão.
            </div>
          ) : null}

          {transcript.user || transcript.assistant ? (
            <div className="bg-muted/40 border-border/70 w-full space-y-2 rounded-lg border px-3 py-2 text-sm">
              {transcript.user ? (
                <p className="text-muted-foreground">
                  <span className="text-foreground/80 font-medium">Você: </span>
                  {transcript.user}
                </p>
              ) : null}
              {transcript.assistant ? (
                <p className="text-muted-foreground">
                  <span className="text-foreground/80 font-medium">Agente: </span>
                  {transcript.assistant}
                </p>
              ) : null}
            </div>
          ) : null}

          {active ? (
            <p className="text-muted-foreground text-xs">Em conversa há {formatElapsed(elapsed)}</p>
          ) : null}
        </div>

        <div className="flex justify-center">
          {active ? (
            <Button variant="destructive" onClick={() => void handleClose()}>
              Encerrar conversa
            </Button>
          ) : (
            <Button variant="outline" onClick={() => void handleClose()}>
              Fechar
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
